import { type Nullable } from '@Types';
import { type ScoreOptionValue, type SelectOption } from './ProjectFieldsModal.types';

// Similarity & Originality statuses, copied from the Angular module this port replaced. The values
// must match the server-side enum. The Grading Portal keeps its own shorter labels in
// GradingPortal/shared/formatters.tsx.
export const SIMILARITY_AND_ORIGINALITY_STATUS_OPTIONS: ReadonlyArray<SelectOption<string>> = Object.freeze([
    { value: 'not_checked', label: 'Not checked' },
    { value: 'check_failed', label: 'Check Failed' },
    { value: 'no_issues', label: 'No Issues' },
    { value: 'issues_detected_needs_review', label: 'Issues Detected - Needs Review' },
    { value: 'issues_detected_no_action_needed', label: 'Issues Detected - No Action Needed' },
    { value: 'issues_detected_hearing_required', label: 'Issues Detected - Hearing Required' },
    { value: 'issues_detected_allow_resubmission', label: 'Issues Detected - Allow Resubmission' },
    { value: 'issues_detected_resubmission_required', label: 'Issues Detected - Resubmission Required' },
    { value: 'issues_detected_no_resubmission_allowed', label: 'Issues Detected - No Resubmission Allowed' },
]);

export const SIMILARITY_AND_ORIGINALITY_STATUSES_CLEARED_FOR_GRADING: ReadonlySet<string> = new Set([
    'no_issues',
    'issues_detected_no_action_needed',
]);

const SIMILARITY_AND_ORIGINALITY_STATUS_LABEL_MAP: Readonly<Record<string, string>> = Object.freeze(
    SIMILARITY_AND_ORIGINALITY_STATUS_OPTIONS.reduce<Record<string, string>>((map, option) => {
        map[option.value] = option.label;
        return map;
    }, {}),
);

export function formatSimilarityAndOriginalityStatus(status: Nullable<string>): Nullable<string> {
    if (status === null || status === undefined || status === '') return status;
    return SIMILARITY_AND_ORIGINALITY_STATUS_LABEL_MAP[status] || status;
}

export function isSimilarityStatusClearedForGrading(status: Nullable<string>): boolean {
    return !!status && SIMILARITY_AND_ORIGINALITY_STATUSES_CLEARED_FOR_GRADING.has(status);
}

export const ID_VERIFIED_OPTIONS: ReadonlyArray<SelectOption<Nullable<boolean>>> = Object.freeze([
    { value: null, label: '' },
    { value: true, label: 'ID verified' },
    { value: false, label: 'ID NOT verified' },
]);

// Builds the Score <select> options, as the directive did. The list holds a blank option, a
// resubmission-required option, and the scores 0 to 5. Each score is labeled passing or not passing,
// against the project's passing score. A non-ExecEd cohort also gets a waived option.
export function buildScoreOptions(passingScore: number, isExecEd: boolean): SelectOption<ScoreOptionValue>[] {
    const options: SelectOption<ScoreOptionValue>[] = [
        { value: null, label: '' },
        { value: 'resubmission_required', label: '0 (Resubmission Required)' },
    ];

    for (let score = 0; score <= 5; score++) {
        options.push({ value: score, label: `${score}${passingScore <= score ? ' (passing)' : ' (not passing)'}` });
    }

    // An ExecEd final_score comes entirely from the one project score. A waiver would leave no
    // final_score, so an ExecEd cohort gets no waived option.
    if (!isExecEd) {
        options.push({ value: 'waived', label: 'Waived' });
    }

    return options;
}

// Filters the project's valid status options, dropping legacy options unless one is the current value.
export function buildStatusOptions(
    validStatusOptions: SelectOption<string>[],
    currentStatus: Nullable<string>,
): SelectOption<string>[] {
    return validStatusOptions.filter(option => option.value === currentStatus || !option.legacy);
}

// Builds the Project Variant <select> options from the project's variant config. The list also holds
// a "not selected" option, and a "(no longer valid)" entry for an orphaned saved value.
export function buildVariantOptions(
    variants: Array<{ slug: string; label: string }> | null | undefined,
    currentVariant: Nullable<string>,
): SelectOption<Nullable<string>>[] {
    const options: SelectOption<Nullable<string>>[] = [{ value: null, label: 'Not selected' }];

    (variants || []).forEach(variant => {
        options.push({ value: variant.slug, label: variant.label });
    });

    if (currentVariant && !options.some(option => option.value === currentVariant)) {
        options.push({ value: currentVariant, label: `${currentVariant} (no longer valid)` });
    }

    return options;
}
